// @ts-strict-ignore
import type { SyntheticEvent } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, FloatingLabelInput, useToast } from "@aviary";
import { isEmailDotValid } from "@shared/components/EmailInput/EmailValidation/EmailValidation";
import { e2eAttributes } from "@shared/e2eAttributes";
import { gRecaptchaExecute } from "@shared/utils/gRecaptchaExecute/gRecaptchaExecute";
import { RecaptchaErrorBox } from "@unauthenticated/shared/components/RecaptchaErrorBox/RecaptchaErrorBox";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import { useOfficeResendAllInvitationsMutation } from "./OfficeResendAllInvitations.mutation";

import * as styles from "./OfficeResendInvitationForm.styles";

interface Props {
  storeId: string;
}

const OfficeResendInvitationForm = ({ storeId }: Props) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const isFormValid = isEmailDotValid(email);
  const { makeToast } = useToast();
  const [recaptchaError, setRecaptchaError] = useState(false);

  const [resendInvitations, { loading }] = useOfficeResendAllInvitationsMutation({
    onCompleted: () => {
      makeToast("success", t(l.officeSignUp.ResendInvitationSuccess, { email }));
    },
  });

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    if (!isFormValid) {
      return;
    }

    gRecaptchaExecute(gToken => {
      resendInvitations({
        variables: { input: { attributes: { email, storeId }, captchaToken: gToken } },
      });
    }, setRecaptchaError);
  };

  return (
    <>
      {!!recaptchaError && <RecaptchaErrorBox />}

      <form onSubmit={handleSubmit} css={styles.form}>
        <FloatingLabelInput
          id="email-input"
          type="email"
          label={t(l.officeSignUp.Email)}
          value={email}
          handleChange={event => setEmail(event.target.value)}
          required
          wrapperStyles={styles.email}
        />
        <Button
          type="submit"
          disabled={!isFormValid}
          isLoading={loading}
          data-e2e={e2eAttributes.officeSignUp.button}
        >
          {t(l.officeSignUp.ResendInvitationSubmit)}
        </Button>
      </form>
    </>
  );
};

export { OfficeResendInvitationForm };
